import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import shippingService from "../../services/shippingService";

/**
 * Fetch all shipping methods (admin only)
 */
export const getAllShippingMethods = createAsyncThunk(
  "adminShipping/getAll",
  async (_, { rejectWithValue }) => {
    try {
      return await shippingService.getAllShippingMethods();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch shipping methods"
      );
    }
  }
);

/**
 * Fetch shipping method by ID (admin only)
 */
export const getShippingMethodById = createAsyncThunk(
  "adminShipping/getById",
  async (id, { rejectWithValue }) => {
    try {
      return await shippingService.getShippingMethodById(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch shipping method"
      );
    }
  }
);

/**
 * Create a new shipping method (admin only)
 */
export const createShippingMethod = createAsyncThunk(
  "adminShipping/create",
  async (methodData, { rejectWithValue }) => {
    try {
      return await shippingService.createShippingMethod(methodData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to create shipping method"
      );
    }
  }
);

/**
 * Update an existing shipping method (admin only)
 */
export const updateShippingMethod = createAsyncThunk(
  "adminShipping/update",
  async ({ id, methodData }, { rejectWithValue }) => {
    try {
      return await shippingService.updateShippingMethod(id, methodData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update shipping method"
      );
    }
  }
);

/**
 * Delete a shipping method (admin only)
 */
export const deleteShippingMethod = createAsyncThunk(
  "adminShipping/delete",
  async (id, { rejectWithValue }) => {
    try {
      return await shippingService.deleteShippingMethod(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete shipping method"
      );
    }
  }
);

/**
 * Initial state for admin shipping slice
 */
const initialState = {
  shippingMethods: [],
  currentMethod: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

/**
 * Admin shipping slice for Redux store
 */
const adminShippingSlice = createSlice({
  name: "adminShipping",
  initialState,
  reducers: {
    /**
     * Reset state flags
     * @param {object} state - Current state
     */
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },

    /**
     * Clear the current method selection
     * @param {object} state - Current state
     */
    clearCurrentMethod: (state) => {
      state.currentMethod = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Shipping Methods
      .addCase(getAllShippingMethods.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllShippingMethods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.shippingMethods = action.payload;
      })
      .addCase(getAllShippingMethods.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to fetch shipping methods";
      })
      // Get Shipping Method By ID
      .addCase(getShippingMethodById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getShippingMethodById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.currentMethod = action.payload;
      })
      .addCase(getShippingMethodById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to fetch shipping method";
      })
      // Create Shipping Method
      .addCase(createShippingMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createShippingMethod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.shippingMethods.push(action.payload);
      })
      .addCase(createShippingMethod.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to create shipping method";
      })
      // Update Shipping Method
      .addCase(updateShippingMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateShippingMethod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.shippingMethods = state.shippingMethods.map((method) =>
          method.id === action.payload.id ? action.payload : method
        );
        state.currentMethod = action.payload;
      })
      .addCase(updateShippingMethod.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to update shipping method";
      })
      // Delete Shipping Method
      .addCase(deleteShippingMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteShippingMethod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.shippingMethods = state.shippingMethods.filter(
          (method) => method.id !== action.payload.id
        );
      })
      .addCase(deleteShippingMethod.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to delete shipping method";
      });
  },
});

export const { reset, clearCurrentMethod } = adminShippingSlice.actions;
export default adminShippingSlice.reducer;
